import { withWindow } from '@utils/withWindow';
import { useCallback, useEffect, useRef } from 'react';
import { InboundIframeMessage, OutboundIframeMessage } from './types';
import { createIframeMessageEventHandler, getPostMessageTarget } from './utils';

type UseIframeMessengerArgs = {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  hasIframeInitialized: boolean;
  onMessage?: (message: InboundIframeMessage) => void;
};

export const useIframeMessenger = ({ iframeRef, hasIframeInitialized, onMessage }: UseIframeMessengerArgs) => {
  const iframeEl = iframeRef.current;
  const outboundMessageQueueRef = useRef<Array<OutboundIframeMessage>>([]);

  const sendMessage = useCallback(
    (message: Omit<OutboundIframeMessage, 'source'>) => {
      if (hasIframeInitialized && iframeEl && iframeEl.contentWindow) {
        iframeEl.contentWindow.postMessage(JSON.stringify({ ...message, source: 'joy' }), getPostMessageTarget());
      } else {
        outboundMessageQueueRef.current.push({ ...message, source: 'joy' });
      }
    },
    [iframeEl, hasIframeInitialized]
  );

  useEffect(
    withWindow(
      global => {
        if (!onMessage || !iframeEl) {
          return () => {};
        }

        const receiveMessage = createIframeMessageEventHandler(onMessage);
        global.addEventListener('message', receiveMessage, false);
        return () => {
          global.removeEventListener('message', receiveMessage, false);
        };
      },
      () => {}
    ),
    [iframeEl, onMessage]
  );

  // Flush enqueued outbound messages when ready
  useEffect(() => {
    if (hasIframeInitialized && iframeEl && iframeEl.contentWindow) {
      const queuedMessages = outboundMessageQueueRef.current;
      outboundMessageQueueRef.current = [];
      if (queuedMessages.length) {
        queuedMessages.forEach(msg => sendMessage(msg));
      }
    }
  });

  return {
    sendMessage,
  };
};
